import { api } from '../client';

export type AlertType =
  | 'LOW_STOCK'
  | 'OVERDUE_SHIPMENT'
  | 'PO_APPROVAL_REQUIRED'
  | 'DELIVERY_DELAYED'
  | 'CRITICAL_STOCK'
  | 'LEAD_TIME_WARNING';

export type AlertSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export interface Alert {
  id: string;
  type: AlertType;
  severity: AlertSeverity;
  title: string;
  message: string;
  companyId: string;
  company?: { id: string; name: string; displayName: string };
  itemId?: string;
  item?: { id: string; sku: string; name: string; minStockLevel?: number };
  entityType?: string;
  entityId?: string;
  isRead: boolean;
  isDismissed: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface QueryAlertParams {
  type?: AlertType;
  severity?: AlertSeverity;
  companyId?: string;
  isRead?: boolean;
  isDismissed?: boolean;
  page?: number;
  limit?: number;
}

export interface AlertCounts {
  total: number;
  unread: number;
  read: number;
  bySeverity: Record<string, number>;
  byType: Record<string, number>;
}

export const alertsApi = {
  async getAll(params?: QueryAlertParams) {
    const response = await api.get<{
      data: Alert[];
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>('/alerts', { params });
    return response.data;
  },

  async getActive(companyId?: string) {
    const response = await api.get<Alert[]>('/alerts/active', {
      params: companyId ? { companyId } : undefined,
    });
    return response.data;
  },

  async getCounts(companyId?: string) {
    const response = await api.get<AlertCounts>('/alerts/counts', {
      params: companyId ? { companyId } : undefined,
    });
    return response.data;
  },

  async getById(id: string) {
    const response = await api.get<Alert>(`/alerts/${id}`);
    return response.data;
  },

  async acknowledge(id: string) {
    const response = await api.patch<Alert>(`/alerts/${id}/acknowledge`);
    return response.data;
  },

  async resolve(id: string) {
    const response = await api.patch<Alert>(`/alerts/${id}/resolve`);
    return response.data;
  },

  async dismiss(id: string) {
    const response = await api.patch<Alert>(`/alerts/${id}/dismiss`);
    return response.data;
  },
};
